import React, { useState } from 'react';
import { useExternalOverrides, useUpsertOverride, useDeleteOverride } from '../../api/usePerformances.js';
import type { PriceOverrideEntry } from '../../api/types.js';

interface Props {
  extId: string;
}

export function PriceOverrideSection({ extId }: Props) {
  const { data: entries = [], isLoading } = useExternalOverrides(extId);
  const upsert = useUpsertOverride();
  const remove = useDeleteOverride();

  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});

  const handleSave = async (entry: PriceOverrideEntry) => {
    const val = draft[entry.service.id];
    if (val === undefined || val === '') return;
    const customPrice = parseFloat(val);
    if (isNaN(customPrice)) return;
    setSaving(s => ({ ...s, [entry.service.id]: true }));
    try {
      await upsert.mutateAsync({ extId, serviceId: entry.service.id, customPrice });
      setDraft(d => { const n = { ...d }; delete n[entry.service.id]; return n; });
    } finally {
      setSaving(s => ({ ...s, [entry.service.id]: false }));
    }
  };

  const handleDelete = async (entry: PriceOverrideEntry) => {
    setSaving(s => ({ ...s, [entry.service.id]: true }));
    try {
      await remove.mutateAsync({ extId, serviceId: entry.service.id });
      setDraft(d => { const n = { ...d }; delete n[entry.service.id]; return n; });
    } finally {
      setSaving(s => ({ ...s, [entry.service.id]: false }));
    }
  };

  if (isLoading) {
    return (
      <div style={{ padding: '1rem', color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
        Caricamento prezzi…
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div style={{
        padding: '1.25rem',
        background: 'var(--glass-bg-card)',
        borderRadius: 'var(--radius-card)',
        border: '1px dashed rgba(255,255,255,0.10)',
        textAlign: 'center',
        fontSize: 'var(--text-sm)',
        color: 'var(--color-text-muted)',
      }}>
        Nessun servizio attivo configurato
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto auto auto',
        gap: '0.5rem',
        padding: '0.375rem 0.75rem',
        fontSize: 'var(--text-xs)',
        fontWeight: 600,
        color: 'var(--color-text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}>
        <span>Servizio</span>
        <span style={{ textAlign: 'right', minWidth: 72 }}>Prezzo base</span>
        <span style={{ textAlign: 'right', minWidth: 110 }}>Personalizzato</span>
        <span style={{ minWidth: 64 }} />
      </div>

      {entries.map(entry => {
        const sid = entry.service.id;
        const hasOverride = entry.override !== null;
        const isSaving = saving[sid] ?? false;
        const inputVal = draft[sid] !== undefined
          ? draft[sid]
          : hasOverride
            ? String(entry.customPrice ?? '')
            : '';

        return (
          <div
            key={sid}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto auto auto',
              gap: '0.5rem',
              alignItems: 'center',
              padding: '0.5rem 0.75rem',
              background: hasOverride ? 'var(--color-primary-highlight)' : 'var(--glass-bg-card)',
              borderRadius: 'var(--radius-card)',
              border: `1px solid ${hasOverride ? 'var(--color-primary)' : 'rgba(255,255,255,0.10)'}`,
              opacity: isSaving ? 0.6 : 1,
              transition: 'opacity var(--transition-interactive)',
            }}
          >
            <span style={{ fontSize: 'var(--text-sm)', fontWeight: hasOverride ? 600 : 400, color: 'var(--color-text)' }}>
              {entry.service.name}
            </span>

            <span style={{
              fontSize: 'var(--text-sm)',
              color: 'var(--color-text-muted)',
              textAlign: 'right',
              minWidth: 72,
              fontVariantNumeric: 'tabular-nums',
            }}>
              {entry.basePrice.toFixed(2)}€
            </span>

            <input
              type="number"
              min="0"
              step="0.01"
              value={inputVal}
              onChange={e => setDraft(d => ({ ...d, [sid]: e.target.value }))}
              disabled={isSaving}
              placeholder={hasOverride ? String(entry.customPrice) : '—'}
              style={{
                width: 110,
                background: 'var(--glass-bg-card)',
                border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: 'var(--radius-interactive)',
                color: 'var(--color-text)',
                fontSize: 'var(--text-sm)',
                padding: '0.25rem 0.5rem',
                outline: 'none',
                textAlign: 'right',
                fontVariantNumeric: 'tabular-nums',
              }}
            />

            <div style={{ display: 'flex', gap: '0.25rem', minWidth: 64, justifyContent: 'flex-end' }}>
              <button
                type="button"
                disabled={isSaving || draft[sid] === undefined || draft[sid] === ''}
                onClick={() => handleSave(entry)}
                style={{
                  background: 'var(--color-primary)',
                  border: 'none',
                  borderRadius: 'var(--radius-interactive)',
                  color: '#fff',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 600,
                  padding: '0.25rem 0.5rem',
                  cursor: draft[sid] !== undefined && draft[sid] !== '' && !isSaving ? 'pointer' : 'not-allowed',
                  opacity: draft[sid] !== undefined && draft[sid] !== '' && !isSaving ? 1 : 0.4,
                  transition: 'opacity var(--transition-interactive)',
                }}
              >
                Salva
              </button>

              {hasOverride && (
                <button
                  type="button"
                  disabled={isSaving}
                  onClick={() => handleDelete(entry)}
                  title="Rimuovi prezzo personalizzato"
                  style={{
                    background: 'var(--color-error-highlight)',
                    border: 'none',
                    borderRadius: 'var(--radius-interactive)',
                    color: 'var(--color-error)',
                    fontSize: 'var(--text-xs)',
                    fontWeight: 600,
                    padding: '0.25rem 0.5rem',
                    cursor: isSaving ? 'not-allowed' : 'pointer',
                    transition: 'opacity var(--transition-interactive)',
                  }}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
